import {
  POOL_MANAGER_ABI,
  POOL_MANAGER_BYTECODE,
  POSITION_MANAGER_ABI,
  POSITION_MANAGER_BYTECODE,
  V4_QUOTER_ABI,
  V4_QUOTER_BYTECODE,
} from "@gotts.ai/chain";
import { stringToHex } from "viem";

import {
  POOL_MODIFY_LIQUIDITY_TEST_ABI,
  POOL_MODIFY_LIQUIDITY_TEST_BYTECODE,
} from "../contracts/v4/pool-modify-liquidity-test";
import {
  POOL_SWAP_TEST_ABI,
  POOL_SWAP_TEST_BYTECODE,
} from "../contracts/v4/pool-swap-test";
import {
  POSITION_DESCRIPTOR_ABI,
  POSITION_DESCRIPTOR_BYTECODE,
} from "../contracts/v4/position-descriptor";
import {
  STATE_VIEW_ABI,
  STATE_VIEW_BYTECODE,
} from "../contracts/v4/state-view";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type {
  DeploymentPhaseResult,
  FoundationAddresses,
  V4Addresses,
} from "./types";
import type { Hex } from "viem";

/** Gas limit for unsubscribe callbacks in PositionManager */
const UNSUBSCRIBE_GAS_LIMIT = 100_000n;

/**
 * Phase 4: Deploy Uniswap V4 contracts.
 *
 * Deploys all 7 V4 contracts from compiled Foundry bytecodes:
 *   1. PoolManager — singleton pool manager
 *   2. PoolSwapTest — test helper for swaps
 *   3. PoolModifyLiquidityTest — test helper for liquidity
 *   4. PositionDescriptor — NFT metadata for positions
 *   5. PositionManager — ERC-721 position NFT manager
 *   6. V4Quoter — off-chain quote simulation
 *   7. StateView — read-only pool state accessor
 *
 * @param ctx - Deploy context
 * @param foundation - Phase 1 addresses (WETH9, Permit2)
 */
export async function deployV4(
  ctx: DeployContext,
  foundation: FoundationAddresses,
): Promise<DeploymentPhaseResult<V4Addresses>> {
  const start = Date.now();
  let totalGas = 0n;
  const txHashes: `0x${string}`[] = [];

  // 1. Deploy PoolManager — constructor(address initialOwner)
  const poolManagerResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    { abi: POOL_MANAGER_ABI, bytecode: POOL_MANAGER_BYTECODE as Hex },
    [ctx.deployer],
  );
  totalGas += poolManagerResult.gasUsed;
  txHashes.push(poolManagerResult.txHash);

  // 2. Deploy PoolSwapTest — constructor(IPoolManager _manager)
  const poolSwapTestResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    { abi: POOL_SWAP_TEST_ABI, bytecode: POOL_SWAP_TEST_BYTECODE as Hex },
    [poolManagerResult.address],
  );
  totalGas += poolSwapTestResult.gasUsed;
  txHashes.push(poolSwapTestResult.txHash);

  // 3. Deploy PoolModifyLiquidityTest — constructor(IPoolManager _manager)
  const poolModifyLiquidityTestResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
      bytecode: POOL_MODIFY_LIQUIDITY_TEST_BYTECODE as Hex,
    },
    [poolManagerResult.address],
  );
  totalGas += poolModifyLiquidityTestResult.gasUsed;
  txHashes.push(poolModifyLiquidityTestResult.txHash);

  // 4. Deploy PositionDescriptor — constructor(IPoolManager, address wrappedNative, bytes32 nativeCurrencyLabel)
  const positionDescriptorResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: POSITION_DESCRIPTOR_ABI,
      bytecode: POSITION_DESCRIPTOR_BYTECODE as Hex,
    },
    [
      poolManagerResult.address,
      foundation.weth9,
      stringToHex("ETH", { size: 32 }),
    ],
  );
  totalGas += positionDescriptorResult.gasUsed;
  txHashes.push(positionDescriptorResult.txHash);

  // 5. Deploy PositionManager — constructor(IPoolManager, IAllowanceTransfer permit2, uint256 unsubscribeGasLimit, IPositionDescriptor, IWETH9)
  const positionManagerResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: POSITION_MANAGER_ABI,
      bytecode: POSITION_MANAGER_BYTECODE as Hex,
    },
    [
      poolManagerResult.address,
      foundation.permit2,
      UNSUBSCRIBE_GAS_LIMIT,
      positionDescriptorResult.address,
      foundation.weth9,
    ],
  );
  totalGas += positionManagerResult.gasUsed;
  txHashes.push(positionManagerResult.txHash);

  // 6. Deploy V4Quoter — constructor(IPoolManager)
  const quoterResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    { abi: V4_QUOTER_ABI, bytecode: V4_QUOTER_BYTECODE as Hex },
    [poolManagerResult.address],
  );
  totalGas += quoterResult.gasUsed;
  txHashes.push(quoterResult.txHash);

  // 7. Deploy StateView — constructor(IPoolManager)
  const stateViewResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    { abi: STATE_VIEW_ABI, bytecode: STATE_VIEW_BYTECODE as Hex },
    [poolManagerResult.address],
  );
  totalGas += stateViewResult.gasUsed;
  txHashes.push(stateViewResult.txHash);

  return {
    phase: 4,
    name: "Uniswap V4",
    addresses: {
      poolManager: poolManagerResult.address,
      poolSwapTest: poolSwapTestResult.address,
      poolModifyLiquidityTest: poolModifyLiquidityTestResult.address,
      positionDescriptor: positionDescriptorResult.address,
      positionManager: positionManagerResult.address,
      quoter: quoterResult.address,
      stateView: stateViewResult.address,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
